import React from 'react'

import { Tooltip } from '@mantine/core'

import IconSparkle from 'src/icons/IconSparkle'
import type { ReviewStatus } from 'src/lib/documentIntelligence'
import { cn } from 'src/utils'

// Color-coded by review outcome (popover only). Brand/blue is the default,
// extraction-only look shared with the editable strip. Full static class strings
// so Tailwind's JIT keeps them.
const STATUS_CLASS: Record<ReviewStatus, string> = {
  accepted: 'bg-doubleNickel-success-50 text-doubleNickel-success-700', // applied
  rejected: 'bg-doubleNickel-gray-100 text-doubleNickel-gray-500', // not used
  pending: 'bg-doubleNickel-warning-50 text-doubleNickel-warning-700', // awaiting review
}
const DEFAULT_CLASS = 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500'

// The "✦ AI · XX%" confidence pill — single source of truth so the editable strip
// and the "AI Extracted" popover render an identical badge.
//
// `status` (popover only) color-codes the review outcome INTO the pill instead of
// a separate badge:
//   accepted → green     rejected → grey + strikethrough     pending → amber
//   undefined / extraction-only → blue (same as the strip)
export const AiConfidencePill = React.forwardRef<
  HTMLSpanElement,
  {
    confidence: number
    tooltipLabel?: string
    status?: ReviewStatus | null
  } & React.HTMLAttributes<HTMLSpanElement>
>(({ confidence, tooltipLabel = 'AI predicted', status, ...props }, ref) => {
  const pct = Math.round(confidence * 100)
  const isRejected = status === 'rejected'

  const label = isRejected
    ? 'AI suggestion · rejected'
    : status === 'accepted'
      ? 'AI predicted · applied'
      : status === 'pending'
        ? 'AI suggestion · pending review'
        : tooltipLabel

  const colorClass = status ? STATUS_CLASS[status] : DEFAULT_CLASS

  const tooltip = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span>{label}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 10, opacity: 0.75 }}>Confidence</span>
          <span style={{ fontSize: 10, fontWeight: 600 }}>{pct}%</span>
        </div>
        <div
          style={{
            width: 120,
            height: 3,
            borderRadius: 9999,
            backgroundColor: 'rgba(255,255,255,0.25)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              borderRadius: 9999,
              backgroundColor: 'white',
            }}
          />
        </div>
      </div>
    </div>
  )

  return (
    <Tooltip label={tooltip} withArrow className="text-xs">
      <span
        ref={ref}
        aria-label={`${label} · ${pct}%`}
        data-review-status={status ?? undefined}
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
          colorClass
        )}
        {...props}
      >
        <IconSparkle size={9} />
        <span className={isRejected ? 'line-through' : undefined}>
          AI · {pct}%
        </span>
      </span>
    </Tooltip>
  )
})

AiConfidencePill.displayName = 'AiConfidencePill'
